import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Phone, 
  Shield, 
  Clock, 
  CreditCard, 
  UserCheck, 
  Ambulance,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { motion } from 'framer-motion';

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_findmed-app/artifacts/eumajomw_086cd4e1-9351-4429-a4f2-f1a3e4606f65%203.JPG";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_findmed-app/artifacts/ge9othd8_LOGO%20SOS%20FINDMED.png";

const features = [
  {
    icon: Shield,
    title: "Decisión Inteligente",
    description: "Resuelve hoy por solo $899 algo que mañana podría costarte entre $2,500 y $3,900 o más en el peor momento."
  },
  {
    icon: Clock,
    title: "Tranquilidad Garantizada",
    description: "No es solo una tarjeta: es tranquilidad, previsión y la seguridad de tener una solución lista para ti y tu familia."
  },
  {
    icon: UserCheck,
    title: "Para Ti y Los Tuyos",
    description: "Protege a quien más quieres. Incluye opción para menores de edad con padre o tutor como responsable."
  },
  {
    icon: Ambulance,
    title: "No Improvises",
    description: "Cuando pasa algo de verdad, no improvisar hace toda la diferencia. SOS FindMed es previsión real."
  }
];

const steps = [
  { number: "1", title: "Regístrate", description: "Crea tu cuenta con tu información personal" },
  { number: "2", title: "Activa tu Tarjeta", description: "Ingresa el código de tu tarjeta y sube tu INE" },
  { number: "3", title: "Espera 72 horas", description: "Tu membresía se activará automáticamente" },
  { number: "4", title: "¡Protegido!", description: "Ya puedes solicitar servicio en caso de emergencia" }
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0D0D0D]">
      {/* Hero Section */}
      <section 
        className="min-h-[100vh] flex items-center justify-center relative overflow-hidden"
        data-testid="hero-section"
      >
        {/* Background Image with overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/90" />
        
        {/* Animated glow effect */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#C81D25]/20 rounded-full blur-[150px] animate-pulse" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <img 
              src={LOGO_URL} 
              alt="SOS FindMed" 
              className="w-80 sm:w-96 lg:w-[500px] h-auto mx-auto mb-8"
              style={{ filter: 'drop-shadow(0 0 10px rgba(255,255,255,0.95)) drop-shadow(0 0 30px rgba(255,255,255,0.5))' }}
              data-testid="hero-logo"
            />
            <p className="text-xl sm:text-2xl text-white/80 mb-2 font-light max-w-2xl mx-auto tracking-wide">
              Servicio de Ambulancia Prepagado
            </p>
            <p className="text-base text-white/60 mb-8 max-w-lg mx-auto">
              Protege a tu familia con acceso a servicios de emergencia médica
            </p>
            
            {/* Price badge premium */}
            <div className="relative inline-block mb-10">
              <div className="absolute inset-0 bg-[#C81D25] blur-2xl opacity-30 rounded-3xl" />
              <div className="relative bg-gradient-to-r from-[#C81D25] to-[#8B0000] rounded-2xl p-8 border border-white/10">
                <p className="text-white/60 text-sm mb-2 tracking-widest uppercase">Membresía Anual</p>
                <div className="flex items-baseline justify-center gap-2">
                  <span className="text-6xl sm:text-7xl font-black text-white">$899</span>
                  <span className="text-xl text-white/70">MXN</span>
                </div>
                <p className="text-white/50 text-xs mt-2">Membresía anual con renovación automática</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild 
                size="lg" 
                className="bg-white text-[#C81D25] hover:bg-gray-100 text-lg px-10 py-7 rounded-full font-bold shadow-[0_8px_40px_rgba(255,255,255,0.2)] transition-all hover:scale-105"
                data-testid="hero-cta-register"
              >
                <Link to="/register">Comprar Ahora</Link>
              </Button>
              <Button 
                asChild 
                variant="outline" 
                size="lg" 
                className="bg-transparent border-2 border-white/30 text-white hover:bg-white/10 text-lg px-10 py-7 rounded-full transition-all"
                data-testid="hero-cta-login"
              >
                <Link to="/login">Ya tengo cuenta</Link>
              </Button>
            </div>
            
            {/* Emergency contact */}
            <div className="mt-12 flex items-center justify-center gap-3">
              <Phone className="w-5 h-5 text-[#C81D25]" />
              <span className="text-white/60 text-sm">Emergencias 24/7:</span>
              <a href="tel:5525671884" className="text-white font-bold text-lg hover:text-[#C81D25] transition-colors">
                55 2567 1884
              </a>
            </div>
          </motion.div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Trust Signals Banner */}
      <section className="bg-slate-950 py-10 px-4 sm:px-6 lg:px-8 border-y border-slate-800" data-testid="trust-signals">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="space-y-2">
              <div className="w-12 h-12 mx-auto bg-[#C81D25]/20 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-[#C81D25]" />
              </div>
              <p className="text-white font-bold text-lg">Atención 24/7</p>
              <p className="text-slate-400 text-sm">Los 365 días del año</p>
            </div>
            <div className="space-y-2">
              <div className="w-12 h-12 mx-auto bg-[#C81D25]/20 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#C81D25]" />
              </div>
              <p className="text-white font-bold text-lg">Paramédicos Certificados</p>
              <p className="text-slate-400 text-sm">Personal capacitado</p>
            </div>
            <div className="space-y-2">
              <div className="w-12 h-12 mx-auto bg-[#C81D25]/20 rounded-xl flex items-center justify-center">
                <UserCheck className="w-6 h-6 text-[#C81D25]" />
              </div>
              <p className="text-white font-bold text-lg">+3,800 Familias</p>
              <p className="text-slate-400 text-sm">Confían en nosotros</p>
            </div>
            <div className="space-y-2">
              <div className="w-12 h-12 mx-auto bg-[#C81D25]/20 rounded-xl flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-[#C81D25]" />
              </div>
              <p className="text-white font-bold text-lg">Pago 100% Seguro</p>
              <p className="text-slate-400 text-sm">Protegido con Stripe</p>
            </div>
          </div>
        </div>
      </section>

      {/* Gift & Use Cases Section - NUEVO: Enfocado en beneficios emocionales */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-[#C81D25]/10 text-[#C81D25] rounded-full text-sm font-semibold mb-4 tracking-wider">
              MÁS QUE UNA MEMBRESÍA
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0D1111] mb-6">
              Es <span className="text-[#C81D25]">tranquilidad</span> para ti y los tuyos
            </h2>
            <p className="text-xl text-gray-500 max-w-3xl mx-auto">
              No es un gasto, es la mejor inversión que puedes hacer por quienes más amas
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Para Ti */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="group"
            >
              <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-100 rounded-3xl p-8 h-full hover:shadow-2xl hover:border-[#C81D25]/20 transition-all duration-300 hover:-translate-y-2">
                <div className="w-16 h-16 bg-gradient-to-br from-[#C81D25] to-[#8B0000] rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                  <UserCheck className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#0D1111] mb-3">Para Ti</h3>
                <p className="text-gray-500 leading-relaxed mb-4">
                  Previsión inteligente. Resuelve hoy lo que mañana podría costarte más de $3,000 en el peor momento.
                </p>
                <p className="text-[#C81D25] font-semibold text-sm">Protégete a ti mismo →</p>
              </div>
            </motion.div>

            {/* Para Regalar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="group"
            >
              <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-100 rounded-3xl p-8 h-full hover:shadow-2xl hover:border-[#C81D25]/20 transition-all duration-300 hover:-translate-y-2">
                <div className="w-16 h-16 bg-gradient-to-br from-[#C81D25] to-[#8B0000] rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                  <CreditCard className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#0D1111] mb-3">Para Regalar</h3>
                <p className="text-gray-500 leading-relaxed mb-4">
                  El mejor regalo para un ser querido. Demuestra cuánto te importa con algo que realmente puede salvar su vida.
                </p>
                <p className="text-[#C81D25] font-semibold text-sm">Regalo que importa →</p>
              </div>
            </motion.div>

            {/* Para tus Padres */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="group"
            >
              <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-100 rounded-3xl p-8 h-full hover:shadow-2xl hover:border-[#C81D25]/20 transition-all duration-300 hover:-translate-y-2">
                <div className="w-16 h-16 bg-gradient-to-br from-[#C81D25] to-[#8B0000] rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#0D1111] mb-3">Para tus Padres</h3>
                <p className="text-gray-500 leading-relaxed mb-4">
                  Tranquilidad para tu familia. Protege a mamá y papá con la seguridad de una respuesta inmediata cuando más lo necesiten.
                </p>
                <p className="text-[#C81D25] font-semibold text-sm">Cuida a quienes te cuidaron →</p>
              </div>
            </motion.div>

            {/* Para tu Empresa */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="group"
            >
              <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-100 rounded-3xl p-8 h-full hover:shadow-2xl hover:border-[#C81D25]/20 transition-all duration-300 hover:-translate-y-2">
                <div className="w-16 h-16 bg-gradient-to-br from-[#C81D25] to-[#8B0000] rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                  <Ambulance className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#0D1111] mb-3">Para tu Equipo</h3>
                <p className="text-gray-500 leading-relaxed mb-4">
                  Beneficio corporativo de alto valor. Protege a tus colaboradores y demuestra que su bienestar es prioridad.
                </p>
                <p className="text-[#C81D25] font-semibold text-sm">Planes empresariales →</p>
              </div>
            </motion.div>
          </div>

          {/* Mensaje de confianza */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mt-16 text-center"
          >
            <p className="text-gray-400 text-sm">
              Servicio respaldado por <strong className="text-[#0D1111]">FindMed</strong> y sus partners autorizados
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-[#C81D25]/10 text-[#C81D25] rounded-full text-sm font-semibold mb-4 tracking-wider">
              ¿POR QUÉ ELEGIRNOS?
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0D1111] mb-6">
              Una decisión <span className="text-[#C81D25]">inteligente</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Porque una emergencia no avisa, pero sí puede vaciarte el bolsillo y hacerte perder tiempo valioso. Por eso SOS FindMed no es un gasto: <strong className="text-[#0D1111]">es tranquilidad.</strong>
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="feature-card h-full border border-gray-100 shadow-lg hover:shadow-2xl bg-white hover:border-[#C81D25]/20 transition-all duration-300">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#C81D25] to-[#8B0000] rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                      <feature.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-[#0D1111] mb-3">{feature.title}</h3>
                    <p className="text-gray-500 leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0D0D0D] via-[#1a1a2e] to-[#0D0D0D]" data-testid="steps-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-white/10 text-white rounded-full text-sm font-semibold mb-4 tracking-wider">
              PROCESO SIMPLE
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              ¿Cómo funciona?
            </h2>
            <p className="text-lg text-white/60 max-w-2xl mx-auto">
              En solo 4 sencillos pasos estarás protegido
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center relative"
              >
                {index < 3 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-[#C81D25] to-transparent" />
                )}
                <div className="w-16 h-16 bg-gradient-to-br from-[#C81D25] to-[#8B0000] rounded-2xl flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-white shadow-[0_10px_40px_rgba(200,29,37,0.4)] relative z-10">
                  {step.number}
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                <p className="text-white/50">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Info */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="info-section">
        <div className="max-w-4xl mx-auto">
          <Card className="border-0 shadow-2xl overflow-hidden">
            <CardContent className="p-0">
              <div className="bg-gradient-to-r from-[#C81D25] to-[#8B0000] p-8 flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                  <AlertTriangle className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">Información Importante</h3>
                  <p className="text-white/70 text-sm">Lee antes de adquirir tu membresía</p>
                </div>
              </div>
              <div className="p-10 bg-white">
                <ul className="space-y-5">
                  <li className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-[#C81D25] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700 text-lg">El servicio es de <strong className="text-[#0D1111]">uso exclusivo para emergencias médicas</strong>.</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-[#C81D25] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700 text-lg">La membresía es <strong className="text-[#0D1111]">personal e intransferible</strong>.</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-[#C81D25] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700 text-lg">El servicio estará disponible <strong className="text-[#0D1111]">72 horas después</strong> de completar tu registro.</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-[#C81D25] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700 text-lg">Se requiere <strong className="text-[#0D1111]">verificación con INE</strong> para activar el servicio.</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-[#C81D25] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700 text-lg">El servicio es <strong className="text-[#0D1111]">exclusivo de la persona que se registra</strong>.</span>
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white" data-testid="contact-section">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block px-4 py-2 bg-[#C81D25]/10 text-[#C81D25] rounded-full text-sm font-semibold mb-4 tracking-wider">
            CONTACTO 24/7
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0D1111] mb-4">
            Línea de Emergencias
          </h2>
          <p className="text-lg text-gray-500 mb-10">
            Para solicitar el servicio de ambulancia, llama a nuestro número de contacto
          </p>
          <a 
            href="tel:5525671884" 
            className="inline-flex items-center gap-4 bg-gradient-to-r from-[#C81D25] to-[#8B0000] text-white px-12 py-8 rounded-3xl text-3xl font-black hover:scale-105 transition-all duration-300 shadow-[0_15px_50px_rgba(200,29,37,0.4)]"
            data-testid="contact-phone"
          >
            <Phone className="w-10 h-10" />
            55 2567 1884
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 py-16 px-4 sm:px-6 lg:px-8 border-t border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="SOS FindMed" className="w-36 h-auto" style={{ filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.8))' }} />
            </div>
            <div className="flex flex-col items-center gap-3">
              <div className="flex items-center gap-4">
                <a href="/terminos" className="text-slate-400 text-sm hover:text-white transition-colors">Términos y Condiciones</a>
                <span className="text-slate-700">|</span>
                <a href="/privacidad" className="text-slate-400 text-sm hover:text-white transition-colors">Aviso de Privacidad</a>
              </div>
              <p className="text-slate-500 text-sm text-center">
                © {new Date().getFullYear()} SOS FindMed. Todos los derechos reservados.
              </p>
            </div>
            <div className="flex items-center gap-3 bg-white/5 px-5 py-3 rounded-xl border border-white/10">
              <CreditCard className="w-5 h-5 text-slate-400" />
              <span className="text-slate-300 text-sm font-medium">Pago seguro con Stripe</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
